import react from 'react';
import { LineChart, LineChartSeries } from '@components/chart/lineChart';
import * as am4charts from '@amcharts/amcharts4/charts';
import moment from 'moment';
import { TTypedTFunction, useTranslation } from '@lib/useTypedTranslation';
import { drawXAxisLabels, drawYAxisLabelsLineChart } from '../../../containers/battery-centric/compositions/lib';
import { IBatteryLevel } from '../../../services/core/devicePerformance';
import { groupBy, isEmpty } from 'lodash';
import { formatDate } from '../../core/device/compositions/performance/charts/deviceChargeLevel';
import './styles.css';

export interface IDeviceLevelChart {
  data: IBatteryLevel[],
  date: number,
  isLoading?: boolean,
  title?: string
}

export interface IBatteryLevelData {
  local: string,
  level: number,
  smartBattery?: {
    serialNumber: string
  }
}

export const DeviceLevelChart = (props: IDeviceLevelChart) => {
  const { t } = useTranslation(['translation', 'performance']);
  const title = t(`DEVICE_CHARGE_LEVEL_FOR`, { date: moment(props.date).utc().format('Do MMMM'), ns: 'translation' });
  const { series, combinedBatteriesData } = getDeviceChargeLevelData(props?.data as IBatteryLevelData[]);

  return (
    <div className="device-charge-level-chart-container">
      <LineChart<any>
        cypressId="deviceChargeLevelChart"
        data={combinedBatteriesData}
        series={series}
        title={title}
        className="device-charge-level"
        hasLegend={true}
        useAxisTooltip={true}
        drawXAxis={(_, xAxis: am4charts.DateAxis) => drawXAxisLabels(xAxis, props.date)}
        drawYAxis={(_, yAxis: am4charts.ValueAxis) => drawYAxisLabelsLineChart(yAxis)}
        adjustChartDimensions={{ length: 97.5, height: 240 }}
        hasNoSelectionOverlay={!props.isLoading}
        getSeriesToolTipAdapter={() => getDeviceChargeLevelTooltipAdapter(t)}
      />
    </div>
  );
};

export const getDeviceChargeLevelData = (data: IBatteryLevelData[]) : { series: LineChartSeries<any>[], combinedBatteriesData:Array<{ local: number } & Record<string, number>> } => {
  const lineAndToolTipColour = ['#5ECAEA', '#709BDF', '#8076E0', '#3BB09A', '#DF7097', '#EA985E', '#5FB952'];
  if (isEmpty(data)) {
    return { series: [{
      visible: true,
      dataKey: 'level',
      description: '-',
      colour: lineAndToolTipColour[0]
    }],
    combinedBatteriesData: [] };
  }
  const dataGroupedByBattery = groupBy(data, (item) => item.smartBattery?.serialNumber ? item.smartBattery.serialNumber : '-');
  const sortedBatteries = Object.keys(dataGroupedByBattery);
  const combinedBatteriesData: Array<{ local: number } & Record<string, number>> = [];

  const localMap = new Map();

  data.forEach(item => {
    const { level, local, smartBattery } = item;
    const serial = smartBattery?.serialNumber ? smartBattery.serialNumber : '-';
    const localAsInt = parseInt(local);
    if (!localMap.has(local)) {
      localMap.set(local, { local: localAsInt });
    }

    localMap.get(local)[serial] = level;
  });

  localMap.forEach(value => combinedBatteriesData.push(value));
  combinedBatteriesData.sort((a, b) => a.local - b.local);

  const series: LineChartSeries<any>[] = sortedBatteries.map((batterySerialNumber: string, index: number) => ({
    visible: true,
    dataKey: batterySerialNumber,
    description: batterySerialNumber,
    colour: lineAndToolTipColour[index]
  }));

  return { series, combinedBatteriesData };
};

export const getDeviceChargeLevelTooltipAdapter = (t: TTypedTFunction) => {
  return (_: any, target: am4charts.LineSeries) => {
    const tooltip = target.tooltipDataItem as any;
    const color = target?.dummyData?.colour;
    const date = formatDate(tooltip.dateX);
    const serialNumberField = target.dataFields.valueY;
    return `<div class="tooltip-container_device-charge-level"><span class="device-charge-level-24-hour-tooltip-title">${date}</span>
    </br><span class="line-chart-tooltip-container_device-charge-level"><div class="key" style="--colour: ${color}"></div>${t('CHARGE_LEVEL')}: {valueY}</span>
    </br><span class="line-chart-tooltip-container-tooltip-text serialnumber">${t('BATTERY_SN', { ns: 'performance' })}: ${serialNumberField}</span></div>`;
  };
};
